<template>
  <div :class="$style.container">
    <div :class="$style.main">
      <issue-header v-if="selectedTask" :issue="selectedTask" :on-update="updateTask" />

      <div :class="$style.actions">
        <issue-action v-if="selectedTask" :issue="selectedTask" :on-update="updateTask" />
        <issue-status v-if="selectedTask" :history="history" :status-id="selectedTask.status_id" />
      </div>

      <issue-chat v-if="selectedTask" :issue="selectedTask">
        <issues-main-chat
          ref="chat"
          class="issues-app-main__chat"
          :taskData="issueData"
          :canRate="false"
          :selectedTask="selectedTask"
          :isLoading="isChatLoading"
          @save="updateTask"
          @deleteFile="deleteFile"
          @deleteMessage="deleteMessage"
          @addTask="$emit('addTask')"
        />
        <issues-main-footer
          class="issues-app-main__footer"
          @send="sendMessage"
          v-if="availableSendMessage"
        />
      </issue-chat>
    </div>
    <div :class="$style.aside">
      <issue-info v-if="selectedTask" :issue="selectedTask" />

      <issue-comment v-if="selectedTask" :issue="selectedTask" :on-update="updateTask" />

      <issue-description v-if="selectedTask" :issue="selectedTask" />

      <issue-history v-if="selectedTask" :history="history" />
    </div>
  </div>
</template>

<script>
import IssuesMainChat                                    from "@/components/common/issues/main/IssuesMainChat";
import IssuesMainFooter                                  from "@/components/common/issues/main/IssuesMainFooter";
import IssuesMainHeader                                  from "@/components/common/issues/main/IssuesMainHeader";
import IssueHeader                                       from "@/components/common/issues/IssueHeader";
import IssueInfo                                         from "@/components/common/issues/IssueInfo";
import IssueHistory                                      from "@/components/common/issues/IssueHistory";
import parseDate                                         from "@/client/helpers/parseDate";
import IssueComment                                      from "@/components/common/issues/IssueComment";
import IssueStatus                                       from "@/components/common/issues/IssueStatus";
import IssueChat                                         from "@/components/common/issues/IssueChat";
import ChatSidebar                                       from "@/components/employee/issues/ChatSidebar";
import IssueAction                                       from "@/components/common/issues/IssueAction";
import { ISSUE_STATUS_CANCELLED, ISSUE_STATUS_COMPLETE } from "@/constants/issues";
import IssueDescription                                  from "@/components/common/issues/IssueDescription";
import * as ws                                           from '@/utils/websockets';
import notifyHttpError                                   from "@/mixins/notifyHttpError";

const sortByCreatedAt = ( isAsc ) => ( a, b ) => {
  const aDate = parseDate(a.created_at);
  const bDate = parseDate(b.created_at);
  return aDate === bDate ? 0 : (isAsc ? aDate > bDate : aDate < bDate) ? 1 : -1;
};

export default {
  name:       "Issue",
  mixins:     [notifyHttpError],
  components: {
    IssueDescription,
    IssueHeader,
    IssueAction,
    IssueChat,
    IssueStatus,
    IssueComment,
    IssueHistory,
    IssueInfo,
    IssuesMainChat,
    IssuesMainFooter,
    IssuesMainHeader,
    ChatSidebar,
  },
  props:      {},
  data() {
    return {
      selectedTask:  null,
      issueData:     [],
      isChatLoading: true,
      history:       [],

      wsMessageListener: null,
    };
  },
  computed: {
    issueId() {
      return +this.$route.params.id;
    },
    currentUserId() {
      return this.$store.getters['appUser/id'];
    },
    isCurrentUserAdmin() {
      const { selectedTask } = this;
      if (!selectedTask) return false;
      return this.$can('issues.admin', 'access', selectedTask.buildings_id);
    },
    availableSendMessage() {
      const { selectedTask, currentUserId } = this;
      return selectedTask &&
        selectedTask.status_id !== ISSUE_STATUS_CANCELLED && (
          selectedTask.performer_id === currentUserId ||
          selectedTask.manager_id === currentUserId ||
          this.isCurrentUserAdmin
        );
    },
  },
  methods:  {
    getIssue() {
      return this.$http.get('tasks/' + this.issueId);
    },

    getChat() {
      const { selectedTask: { id } } = this;
      if (!id) return Promise.resolve([]);
      return this.$http.get('chat', { params: { issue_id: id } }).then(( { data } ) => data);
    },

    getHistory() {
      const { selectedTask: { id } } = this;
      if (!id) return Promise.resolve([]);
      return this.$http.get('tasks_history', { params: { issue_id: id } })
        .then(( { data } ) => data.sort(sortByCreatedAt()));
    },

    getAttachments() {
      const { selectedTask: { id } } = this;
      if (!id) return Promise.resolve([]);
      return this.$http.get(`task-attachment/${id}`).then(( { data } ) => data);
    },

    loadIssueData( withLoader = false ) {
      if (!this.selectedTask.id) {
        this.isChatLoading = false;
        return false;
      }

      if (withLoader) this.isChatLoading = true;

      return Promise.all([
        this.getChat(),
        this.getHistory(),
        this.getAttachments(),
      ]).then(( [chat, history, attachments] ) => {
        chat = chat.map(c => {
          c.isChat = true;
          return c;
        });
        history = history.map(h => {
          h.isHistory = true;
          return h;
        });
        attachments = attachments.map(f => {
          f.isFile = true;
          return f;
        });

        let task = this.selectedTask;

        this.history = history;

        this.$emit('update:selectedTask', task);

        let issueData = [...history, ...chat, ...attachments].sort(sortByCreatedAt(true));
        // let descriptionItem = {
        //   id:         -1,
        //   message:    this.selectedTask.description,
        //   created_at: this.selectedTask.created_at,
        //   issue_id:   this.selectedTask.id,
        //   isChat:     true,
        //   users_id:   this.selectedTask.users_id,
        //   user:       this.selectedTask.owner,
        // };
        //
        // if (issueData.length) {
        //   let indexDescription = 0;
        //   if (issueData[0].isHistory) {
        //     indexDescription = 1;
        //   }
        //   issueData.splice(indexDescription, 0, descriptionItem);
        // }
        let taskChatLength = this.issueData.length;
        this.issueData = issueData;

        if (this.issueData.length !== taskChatLength) {
          this.chatScrollDown();
          this.readChat();
        }

        this.isChatLoading = false;
      });
    },

    chatScrollDown() {
      this.$refs.chat.scrollDown();
    },

    sendMessage( data ) {
      if ((data.message === null || !data.message.length) && !data.files) return false;
      let body = new FormData();
      body.append('issue_id', this.selectedTask.id);
      if (data.message !== null && data.message.trim().length) {
        body.append('message', data.message);
      }
      body.append('users_id', this.$store.getters['appUser/id']);
      if (data.files) {
        for (let i = 0; i < data.files.length; i++) {
          body.append(`attachment[]`, data.files[i]);
        }
      }
      return this.$http.post('chat', body);
    },

    updateTask( task ) {
      task = {
        ...this.selectedTask,
        ...task,
      };
      return this.$http.put(`tasks/${task.id}`, task);
    },

    readChat() {
      return this.$http.put('chat_view_messages/' + this.issueId);
    },

    deleteFile( file ) {
      return this.$http.delete('remove-attachment/' + file.id).catch(this.notifyHttpError);
    },

    deleteMessage( message ) {
      return this.$http.delete('chat/' + message.id).catch(this.notifyHttpError);
    },

    loadIssue( setLoaded = false ) {
      return this.$http.get('tasks/' + this.issueId).then(
        ( { data } ) => {
          this.selectedTask = data;
          this.loadIssueData(setLoaded);
        },
        ( e ) => {
          if (e.response) {
            if (e.response.status === 403) {
              this.$store.commit('SET_FORBIDDEN', true);
            } else if (e.response.status === 404) {
              this.$store.commit('SET_ERROR404', true);
            } else {
              this.$vs.notify({ color: 'danger', title: 'Ошибка сервера', text: 'Повторите позже' });
            }
          } else {
            console.error(e);
            this.$vs.notify({ color: 'danger', title: 'Произошла ошибка', text: 'Неизвестная ошибка' });
          }
        },
      );
    },

    onWsMessage( message ) {
      const { id, key } = message;
      if (id !== this.issueId) return;
      if (key === 'new_issue_message') {
        this.loadIssueData();
      }
      if (key === 'new_issue_file') {
        this.loadIssueData();
      }
      if (key === 'change_issue') {
        this.loadIssue();
      }
    },
  },
  created() {
    this.loadIssue(true);
    this.wsMessageListener = ws.onMessage(this.onWsMessage);
  },
  beforeDestroy() {
    if (this.wsMessageListener) this.wsMessageListener();
  },
};
</script>

<style lang="scss" module>
.container {
  //flex: 1;
  position: absolute;
  top: 15px;
  right: 15px;
  bottom: 15px;
  left: 15px;
  background: #fff;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  padding: 20px 0;
  display: flex;
  flex-direction: row;
  overflow: auto;
  min-height: 700px;
}

.main {
  flex: 2;
  min-width: 1000px;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
}

.aside {
  overflow: auto;
  flex: 1;
  min-width: 440px;
  padding-left: 20px;
  padding-right: 20px;
  display: flex;
  flex-direction: column;
}

.actions {
  display: flex;
  align-items: center;
  margin-bottom: 35px;
}
</style>
